import React from 'react';
import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Shield, Users, Clock, Star, PhoneCall, Calendar, Baby, Heart } from 'lucide-react';
import SEO from '../components/SEO';

const Home = () => {
  const appUrl = process.env.REACT_APP_EXTERNAL_APP_URL || 'https://aupairshare.base44.app';

  const handleAppRedirect = () => {
    window.open(appUrl, '_blank');
  };

  const howItWorksSteps = [
    {
      icon: <Calendar className="w-8 h-8" />,
      title: 'Book in Advance',
      description: 'Plan ahead for regular school runs, after-school care, or known commitments.'
    },
    {
      icon: <Clock className="w-8 h-8" />,
      title: 'Same-Day Support',
      description: 'Need help today? Book an au pair for unexpected schedule changes.'
    },
    {
      icon: <PhoneCall className="w-8 h-8" />,
      title: 'Emergency Collections',
      description: 'Stuck in traffic or a meeting? We have got you covered for emergency pickups.'
    },
    {
      icon: <Baby className="w-8 h-8" />,
      title: 'Babysitting',
      description: 'Evening plans? Book trusted au pairs for babysitting when you need it.'
    }
  ];

  const whyChooseUs = [
    {
      icon: <Users className="w-10 h-10" />,
      title: 'Not Just Drivers',
      description: 'Our au pairs provide genuine childcare - engaging with your children, not just transporting them.'
    },
    {
      icon: <Users className="w-10 h-10" />,
      title: 'Carefully Vetted Au Pairs',
      description: 'All au pairs — whether students, qualified childcare professionals, or Senior Carers — are verified with background checks and licenses.'
    },
    {
      icon: <Clock className="w-10 h-10" />,
      title: 'Save Massively on Costs',
      description: 'Get the quality and reliability of au pair care without the R15,000+ monthly cost of full-time employment. Pay only for the hours you actually need.'
    },
    {
      icon: <Heart className="w-10 h-10" />,
      title: 'No Full-Time Commitment',
      description: 'Skip the contracts, accommodation costs, and management hassles. Get professional au pair care exactly when you need it.'
    }
  ];

  const trustFeatures = [
    {
      icon: <Shield className="w-8 h-8" />,
      title: 'Vetting & Verification',
      description: 'Background checks, qualifications verification, and psychometric assessments'
    },
    {
      icon: <Star className="w-8 h-8" />,
      title: 'Live Tracking',
      description: 'Monitor every booking in real-time through the app'
    },
    {
      icon: <Users className="w-8 h-8" />,
      title: 'Compliance',
      description: 'Aligned with South African Children\'s Act and POPIA'
    },
    {
      icon: <Heart className="w-8 h-8" />,
      title: 'Accountability',
      description: 'Rating system and incident reporting for peace of mind'
    }
  ];

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-50 to-white">
      <SEO 
        title="Flexible Childcare"
        description="Affordable, flexible au pair childcare in Johannesburg, Pretoria, Cape Town & Durban. Vetted caregivers, no long-term commitments. Save 60-80% on childcare costs."
        keywords="au pair, childcare, nanny, babysitter, Johannesburg, Pretoria, Cape Town, Durban, South Africa, flexible childcare"
        path="/"
      />
      {/* Hero Section */}
      <section className="relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-slate-100 via-lavender-50 to-slate-50 opacity-60"></div>
        <div className="container mx-auto px-4 sm:px-6 lg:px-8 py-20 lg:py-32 relative z-10">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div className="space-y-8">
              <div className="space-y-5">
                <h1 className="text-5xl lg:text-6xl font-bold text-slate-900 leading-tight">
                  Let us be your village.
                </h1>
                <p className="text-2xl lg:text-3xl font-semibold text-slate-600">
                  Flexible care, shared costs.
                </p>
                <div className="text-lg text-slate-700 space-y-4 leading-relaxed">
                  <p>
                    At Au Pair Share, we give families access to the high quality services of vetted au pairs —without the cost, commitment, or complexity of full-time arrangements.
                  </p>
                  <p>
                    Our carefully selected au pairs provide reliable, nurturing, and responsible childcare, available exactly when life demands it. Whether it's an early school pickup, a late meeting, or an unexpected gap in your schedule, you simply book the time you need—and pay only for the hours used.
                  </p>
                  <div className="pt-2 space-y-1 font-semibold text-slate-800">
                    <p>No monthly salaries.</p>
                    <p>No long-term commitments.</p>
                    <p>No paying for time you don't need.</p>
                  </div>
                </div>
              </div>
              <div className="flex flex-col sm:flex-row gap-4">
                <Button 
                  size="lg" 
                  onClick={handleAppRedirect}
                  className="bg-slate-700 hover:bg-slate-800 text-white px-8 py-6 text-lg"
                >
                  Open the App
                </Button>
                <Button 
                  size="lg" 
                  variant="outline"
                  onClick={handleAppRedirect}
                  className="border-slate-300 text-slate-700 hover:bg-slate-50 px-8 py-6 text-lg"
                >
                  Become an Au Pair
                </Button>
              </div>
            </div>
            <div className="relative">
              <div className="rounded-2xl overflow-hidden shadow-2xl">
                <img 
                  src="https://customer-assets.emergentagent.com/job_flexible-families/artifacts/94duphf2_Etre-au-pair.webp" 
                  alt="Au pair reading with child"
                  className="w-full h-[500px] object-cover"
                />
              </div>
              <div className="absolute -bottom-6 -left-6 bg-white p-6 rounded-xl shadow-lg">
                <div className="flex items-center gap-3">
                  <div className="bg-green-100 p-3 rounded-full">
                    <Shield className="w-6 h-6 text-green-600" />
                  </div>
                  <div>
                    <p className="font-semibold text-slate-900">Verified & Trusted</p>
                    <p className="text-sm text-slate-500">All au pairs vetted</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* How It Works */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl lg:text-5xl font-bold text-slate-900 mb-4">
              How It Works
            </h2>
            <p className="text-xl text-slate-600 max-w-2xl mx-auto">
              Simple, flexible childcare support for your family's needs
            </p>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {howItWorksSteps.map((step, index) => (
              <Card key={index} className="border-slate-200 hover:shadow-lg transition-all duration-300">
                <CardContent className="p-8 text-center space-y-4">
                  <div className="bg-slate-100 w-16 h-16 rounded-full flex items-center justify-center mx-auto text-slate-700">
                    {step.icon}
                  </div>
                  <h3 className="text-xl font-semibold text-slate-900">{step.title}</h3>
                  <p className="text-slate-600">{step.description}</p>
                  <Button 
                    variant="link" 
                    onClick={handleAppRedirect}
                    className="text-slate-700 hover:text-slate-900"
                  >
                    Book via the App →
                  </Button>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Cost Savings Highlight */}
      <section className="py-16 bg-gradient-to-br from-green-50 to-emerald-50">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-5xl mx-auto bg-white rounded-2xl shadow-xl p-8 lg:p-12">
            <div className="text-center mb-8">
              <h2 className="text-3xl lg:text-4xl font-bold text-slate-900 mb-3">
                Au Pair Quality Without the Full-Time Cost
              </h2>
              <p className="text-xl text-slate-600">
                Why pay R15,000+ per month when you only need help a few hours a week?
              </p>
            </div>
            <div className="grid md:grid-cols-2 gap-8">
              <div className="bg-slate-50 rounded-xl p-6">
                <h3 className="text-xl font-bold text-slate-900 mb-4">Traditional Full-Time Au Pair</h3>
                <ul className="space-y-3 text-slate-700">
                  <li className="flex items-start gap-2">
                    <span className="text-red-500 font-bold mt-1">✗</span>
                    <span>R15,000 - R20,000+ monthly salary</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-red-500 font-bold mt-1">✗</span>
                    <span>Accommodation and meals required</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-red-500 font-bold mt-1">✗</span>
                    <span>12-month minimum contracts</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-red-500 font-bold mt-1">✗</span>
                    <span>Leave, benefits, and management</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-red-500 font-bold mt-1">✗</span>
                    <span>Pay even when you do not need help</span>
                  </li>
                </ul>
              </div>
              <div className="bg-green-50 rounded-xl p-6 border-2 border-green-200">
                <h3 className="text-xl font-bold text-slate-900 mb-4">Au Pair Share</h3>
                <ul className="space-y-3 text-slate-700">
                  <li className="flex items-start gap-2">
                    <span className="text-green-600 font-bold mt-1">✓</span>
                    <span>Pay only for hours you actually use</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-green-600 font-bold mt-1">✓</span>
                    <span>No accommodation or meal costs</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-green-600 font-bold mt-1">✓</span>
                    <span>Zero long-term commitments</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-green-600 font-bold mt-1">✓</span>
                    <span>No leave or benefits management</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-green-600 font-bold mt-1">✓</span>
                    <span>Same au pair quality and reliability</span>
                  </li>
                </ul>
              </div>
            </div>
            <div className="mt-8 text-center">
              <p className="text-2xl font-bold text-green-700 mb-4">
                Save 60-80% compared to full-time employment
              </p>
              <p className="text-slate-600">
                Most families need help 5-10 hours per week. Why pay for 160+ hours?
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Why Au Pair Share */}
      <section className="py-20 bg-slate-50">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl lg:text-5xl font-bold text-slate-900 mb-4">
              Why Au Pair Share
            </h2>
            <p className="text-xl text-slate-600 max-w-3xl mx-auto">
              Professional au pair care with complete flexibility and massive savings
            </p>
          </div>
          <div className="grid md:grid-cols-2 gap-8 max-w-5xl mx-auto">
            {whyChooseUs.map((item, index) => (
              <div key={index} className="bg-white p-8 rounded-xl shadow-sm hover:shadow-md transition-all duration-300">
                <div className="flex gap-6">
                  <div className="bg-slate-100 p-4 rounded-lg h-fit text-slate-700">
                    {item.icon}
                  </div>
                  <div className="space-y-2">
                    <h3 className="text-2xl font-semibold text-slate-900">{item.title}</h3>
                    <p className="text-slate-600 leading-relaxed">{item.description}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Trust & Safety */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div className="order-2 lg:order-1">
              <img 
                src="https://customer-assets.emergentagent.com/job_flexible-families/artifacts/rx31q0l4_GettyImages-1252896371-1536x1024.jpeg" 
                alt="Au pair helping child with homework"
                className="rounded-2xl shadow-xl w-full h-[400px] object-cover"
              />
            </div>
            <div className="order-1 lg:order-2 space-y-8">
              <div>
                <h2 className="text-4xl lg:text-5xl font-bold text-slate-900 mb-4">
                  Trust & Safety
                </h2>
                <p className="text-xl text-slate-600">
                  Your child's safety is our absolute priority
                </p>
              </div>
              <div className="grid sm:grid-cols-2 gap-6">
                {trustFeatures.map((feature, index) => (
                  <div key={index} className="space-y-3">
                    <div className="bg-slate-100 w-12 h-12 rounded-lg flex items-center justify-center text-slate-700">
                      {feature.icon}
                    </div>
                    <h3 className="text-lg font-semibold text-slate-900">{feature.title}</h3>
                    <p className="text-slate-600 text-sm">{feature.description}</p>
                  </div>
                ))}
              </div>
              <Button 
                size="lg"
                onClick={handleAppRedirect}
                className="bg-slate-700 hover:bg-slate-800 text-white"
              >
                Learn More in the App
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="py-20 bg-slate-900 text-white">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <div className="max-w-3xl mx-auto space-y-8">
            <h2 className="text-4xl lg:text-5xl font-bold">
              Ready to find your village?
            </h2>
            <p className="text-xl text-slate-300">
              Join families across South Africa who trust Au Pair Share for flexible, reliable childcare.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Button 
                size="lg"
                onClick={handleAppRedirect}
                className="bg-white text-slate-900 hover:bg-slate-100 px-8 py-6 text-lg"
              >
                Open the App
              </Button>
              <Button 
                size="lg"
                variant="outline"
                onClick={handleAppRedirect}
                className="border-white text-white hover:bg-slate-800 px-8 py-6 text-lg"
              >
                Download the App
              </Button>
            </div>
            <p className="text-sm text-slate-400">
              All bookings and matching happen in the app
            </p>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Home;
